import { Alert, AlertIcon, Box, Button, Heading, SimpleGrid, Spinner, Text } from '@chakra-ui/react'
import { useEffect, useRef } from 'react'
import Card from '../../common/components/Card'
import { MfaContact, MfaSelfServiceState } from './mfa-self-service-api'

const MFA_SERVER_DISABLED_MESSAGE = 'Multi-factor authentication is disabled on the server.'
const MFA_PROVIDER_UNAVAILABLE_MESSAGE =
  'Security settings are temporarily unavailable. Please try again.'

export type MfaViewStatus =
  | 'loading'
  | 'ready'
  | 'ui-disabled'
  | 'server-disabled'
  | 'ineligible'
  | 'error'

interface Props {
  status: MfaViewStatus
  state?: MfaSelfServiceState
  onRetry: () => void
}

const KNOWN_METHODS = ['SMS_MFA', 'EMAIL_OTP', 'SOFTWARE_TOKEN_MFA']

const ContactDetails = ({ contact, missing }: { contact: MfaContact | null; missing: string }) => {
  if (!contact) return <Text>{missing} This method is unavailable.</Text>
  const verification =
    contact.verified === true
      ? 'Verified'
      : contact.verified === false
      ? 'Not verified'
      : 'Verification status unavailable'
  return (
    <>
      <Text>{contact.masked}</Text>
      <Text color="gray.600">{verification}</Text>
    </>
  )
}

const MethodCard = ({
  title,
  method,
  state,
  contact,
  missing,
}: {
  title: string
  method: string
  state: MfaSelfServiceState
  contact?: MfaContact | null
  missing?: string
}) => {
  const enabled = state.enabledMethods.includes(method)
  const status =
    method === 'SMS_MFA' && state.legacyMfaOptions && !enabled
      ? 'Status unavailable'
      : enabled
      ? 'Enabled'
      : 'Not enabled'

  return (
    <Card as="section" p={5} minW={0}>
      <Heading as="h3" size="sm" mb={3}>
        {title}
      </Heading>
      <Text fontWeight="semibold">{status}</Text>
      {state.preferredMethod === method && <Text color="blue.700">Preferred</Text>}
      {missing && <ContactDetails contact={contact ?? null} missing={missing} />}
    </Card>
  )
}

const ReadyState = ({ state }: { state: MfaSelfServiceState }) => {
  const unsupported = state.enabledMethods.some((method) => !KNOWN_METHODS.includes(method))
  const preferenceUnlisted =
    state.preferredMethod !== null && !state.enabledMethods.includes(state.preferredMethod)
  const unsupportedPreference =
    state.preferredMethod !== null && !KNOWN_METHODS.includes(state.preferredMethod)

  return (
    <>
      <Text color="gray.600" mb={5}>
        View the methods and registered account contacts used for sign-in. These settings are
        read-only.
      </Text>
      {state.enabledMethods.length === 0 && !state.legacyMfaOptions && (
        <Alert status="info" mb={4}>
          <AlertIcon />
          No MFA methods are enabled.
        </Alert>
      )}
      {!state.preferredMethod && (
        <Alert status="info" mb={4}>
          <AlertIcon />
          No preferred MFA method is set.
        </Alert>
      )}
      {preferenceUnlisted && (
        <Alert status="warning" mb={4}>
          <AlertIcon />
          The preferred MFA method is not in the enabled list. These settings are read-only; contact
          support for help.
        </Alert>
      )}
      {unsupported && (
        <Alert status="warning" mb={4}>
          <AlertIcon />
          Some enabled MFA methods are not supported in this view. These settings are read-only;
          contact support for help.
        </Alert>
      )}
      {unsupportedPreference && (
        <Alert status="warning" mb={4}>
          <AlertIcon />
          Preferred method: {state.preferredMethod} (not supported in this view).
        </Alert>
      )}
      {state.legacyMfaOptions && (
        <Alert status="warning" mb={4}>
          <AlertIcon />
          Legacy MFA settings require review. The SMS status may be unavailable; these settings are
          read-only.
        </Alert>
      )}
      <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4}>
        <MethodCard
          title="SMS message"
          method="SMS_MFA"
          state={state}
          contact={state.contacts.phone}
          missing="No registered mobile number."
        />
        <MethodCard
          title="Email message"
          method="EMAIL_OTP"
          state={state}
          contact={state.contacts.email}
          missing="No registered email address."
        />
        <MethodCard title="Authenticator app" method="SOFTWARE_TOKEN_MFA" state={state} />
      </SimpleGrid>
    </>
  )
}

export const MfaSelfServicePage = ({ status, state, onRetry }: Props) => {
  const headingRef = useRef<HTMLHeadingElement>(null)

  useEffect(() => {
    headingRef.current?.focus()
  }, [])

  const message =
    status === 'ui-disabled'
      ? 'Multi-factor authentication is not available in this version of RxPad.'
      : status === 'server-disabled'
      ? MFA_SERVER_DISABLED_MESSAGE
      : status === 'ineligible'
      ? 'Multi-factor authentication is not available for this account.'
      : MFA_PROVIDER_UNAVAILABLE_MESSAGE

  return (
    <Box>
      <Heading as="h2" size="md" mb={4} tabIndex={-1} ref={headingRef}>
        Multi-factor authentication
      </Heading>
      {status === 'loading' ? (
        <Box role="status" aria-live="polite">
          <Spinner size="sm" mr={2} />
          Loading security settings...
        </Box>
      ) : status === 'ready' && state ? (
        <ReadyState state={state} />
      ) : (
        <Alert status={status === 'error' ? 'error' : 'warning'} role="alert">
          <AlertIcon />
          <Box>
            <Text>{message}</Text>
            {status === 'error' && (
              <Button
                mt={3}
                size="sm"
                onClick={() => {
                  headingRef.current?.focus()
                  onRetry()
                }}
              >
                Retry
              </Button>
            )}
          </Box>
        </Alert>
      )}
    </Box>
  )
}
